import React, { useState, useRef, useEffect } from "react";
import axios from 'axios';
import { Col, Container, Row } from "reactstrap";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { Toast } from "primereact/toast";
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { AutoComplete } from "primereact/autocomplete";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import {
    Input,
    InputGroup,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Button as RSButton,
} from "reactstrap";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import { FaFilter, FaRedo, FaExchangeAlt, FaTimes, FaUserCheck, FaInbox } from "react-icons/fa";

const columnAccentPalette = [
  { bar: "#2563eb", bg: "#eff6ff", text: "#1d4ed8" },
  { bar: "#0f766e", bg: "#f0fdfa", text: "#0f766e" },
  { bar: "#d97706", bg: "#fffbeb", text: "#b45309" },
  { bar: "#7c3aed", bg: "#f5f3ff", text: "#6d28d9" },
  { bar: "#db2777", bg: "#fdf2f8", text: "#be185d" },
  { bar: "#0891b2", bg: "#ecfeff", text: "#0e7490" },
  { bar: "#dc2626", bg: "#fef2f2", text: "#b91c1c" },
  { bar: "#65a30d", bg: "#f7fee7", text: "#4d7c0f" },
];

const getColumnAccent = (index) => columnAccentPalette[index % columnAccentPalette.length];

const getInitials = value =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const avatarPalette = [
  { bg: "#eff6ff", color: "#1d4ed8" },
  { bg: "#f0fdfa", color: "#0f766e" },
  { bg: "#fffbeb", color: "#b45309" },
  { bg: "#f5f3ff", color: "#6d28d9" },
  { bg: "#fdf2f8", color: "#be185d" },
  { bg: "#ecfeff", color: "#0e7490" },
];

const getAvatarStyle = value => {
  let hash = 0;
  String(value || "").split("").forEach(char => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

const findCandidateById = (kanbanData, itemId) => {
  if (!itemId) return null;
  return Object.values(kanbanData || {})
    .flat()
    .find(item => item?.id === itemId) || null;
};

const JOBS_PAGE_SIZE = 10;

const normalizeJobsResponse = data => {
  if (Array.isArray(data)) {
    return { rows: data, count: data.length, hasNext: false };
  }

  const resultRows = Array.isArray(data?.results?.data)
    ? data.results.data
    : Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.data)
        ? data.data
        : [];

  return {
    rows: resultRows,
    count: data?.count ?? data?.results?.count ?? resultRows.length,
    hasNext: Boolean(data?.next || data?.results?.next)
  };
};

const getCandidateFullName = candidate => {
  const fullName = [
    candidate?.first_name,
    candidate?.middle_name,
    candidate?.last_name
  ].filter(Boolean).join(" ");

  return candidate?.candidate_name || fullName || candidate?.name || "Candidate";
};

const KanbanView = () => {
  const toast = useRef(null);
  const containerRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const skipNextFilterFetch = useRef(false);
  const hasFilterInteracted = useRef(false);

  // State to manage the kanban data
  const [kanbanData, setKanbanData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [stageCounts, setStageCounts] = useState({});
  const [pipelinePage, setPipelinePage] = useState(1);
  const pipelinePageSize = 10;
  // State to manage dynamic columns
  const [columns, setColumns] = useState([]);

  // State for dynamic filter options
  const [recruiters, setRecruiters] = useState([]);
  const [jobStatusOptions, setJobStatusOptions] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);
  const [jobOrderPage, setJobOrderPage] = useState(1);
  const [jobOrderTotalCount, setJobOrderTotalCount] = useState(0);
  const [jobOrderHasNextPage, setJobOrderHasNextPage] = useState(false);
  const [jobOrderLoading, setJobOrderLoading] = useState(false);

  document.title = "ATS - Candidates Pipeline"

   const userInfos = JSON.parse(sessionStorage.getItem("user-info"));
    const [companyId, setCompanyId] = useState(null);
    const [companySelectionVersion, setCompanySelectionVersion] = useState(0);
  
    const handleCompanyChange = (company) => {
      if (company?.company_id) {
          const nextCompanyId = String(company.company_id).toLowerCase() === "all"
            ? "all"
            : company.company_id;
          activeJobRequestRef.current?.controller.abort();
          activeCandidateRequestRef.current?.controller.abort();
          jobCacheRef.current.clear();
          stageCacheRef.current.clear();
          candidateDataCacheRef.current.clear();
          setSelectedCompany({ ...company, company_id: nextCompanyId });
          setCompanyId(nextCompanyId);
          setCompanySelectionVersion(version => version + 1);
          setPipelinePage(1);
          setSelectedJob(null);
          setCandidateName("");
          setKanbanData({});
          setOriginalData({});
      }
    };
const [loading, setLoading] = useState(false);
  const jobCacheRef = useRef(new Map());
  const jobRequestsRef = useRef(new Map());
  const activeJobRequestRef = useRef(null);
  const stageCacheRef = useRef(new Map());
  const stageRequestsRef = useRef(new Map());
  const candidateDataCacheRef = useRef(new Map());
  const candidateRequestsRef = useRef(new Map());
  const activeCandidateRequestRef = useRef(null);

  const fetchColumns = async () => {
    if (!companyId) return [];
    if (stageCacheRef.current.has(String(companyId))) {
      const cachedColumns = stageCacheRef.current.get(String(companyId));
      setColumns(cachedColumns);
      return cachedColumns;
    }
    if (stageRequestsRef.current.has(String(companyId))) {
      return stageRequestsRef.current.get(String(companyId));
    }

    const request = (async () => {
      try {
      let page = 1;
      let hasNextPage = true;
      const stageMap = new Map();

      while (hasNextPage) {
        const response = await axios.get(`/api/v1/get-all-stages/?company_id=${companyId}&page=${page}&size=100`);
        const data = response.data;
        const rows = Array.isArray(data) ? data : data?.results || [];

        rows.forEach(stage => {
          const id = stage?.id ?? stage?.stage_id ?? stage?.name;
          const name = stage?.name ?? stage?.stage_name ?? stage?.label;
          if (id && name) stageMap.set(String(id), { ...stage, id, name });
        });

        hasNextPage = Boolean(data?.next) && rows.length > 0;
        page += 1;
        if (page > 25) hasNextPage = false;
      }

      const apiColumns = Array.from(stageMap.values());
      
      const transformedColumns = apiColumns.map(stage => ({
        id: stage.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, ''),
        title: stage.name
      }));

      setColumns(transformedColumns);

      const initialData = transformedColumns.reduce((acc, column) => {
        acc[column.id] = [];
        return acc;
      }, {});
      setKanbanData(initialData);
      setOriginalData(initialData);
      stageCacheRef.current.set(String(companyId), transformedColumns);
      return transformedColumns;
      } catch (error) {
      console.error('Error fetching columns:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch Kanban columns',
        life: 3000
      });
      setColumns([]);
      setKanbanData({});
      setOriginalData({});
      return [];
      }
    })().finally(() => stageRequestsRef.current.delete(String(companyId)));

    stageRequestsRef.current.set(String(companyId), request);
    return request;
  };

  const fetchRecruiters = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-hiring-managers/?company_id=${companyId}`);
      const apiRecruiters = response.data.results;
      const transformedRecruiters = apiRecruiters.map(recruiter => ({
        name: recruiter?.first_name || "",
        id: recruiter.id
      }));
      setRecruiters(transformedRecruiters);
    } catch (error) {
      console.error('Error fetching recruiters:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch recruiters',
        life: 3000
      });
    }
  };

  const fetchJobStatuses = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-Job-status/?company_id=${companyId}`);
      const apiStatuses = response.data.results;
      const transformedStatuses = apiStatuses.map(status => ({
        label: status.job_status,
        id: status.id
      }));
      setJobStatusOptions(transformedStatuses);
    } catch (error) {
      console.error('Error fetching job statuses:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch job statuses',
        life: 3000
      });
    }
  };

  const fetchJobs = async (page = 1, forceSelectFirstJob = false) => {
    if (!companyId) return;
    const cacheKey = `${companyId}:${page}`;
    const cached = jobCacheRef.current.get(cacheKey);
    if (cached) {
      setJobOptions(cached.rows);
      setJobOrderPage(cached.page);
      setJobOrderTotalCount(cached.count);
      setJobOrderHasNextPage(cached.hasNext);
      if (page === 1 && (forceSelectFirstJob || !selectedJob) && cached.rows[0]) {
        hasFilterInteracted.current = true;
        setPipelinePage(1);
        setSelectedJob(cached.rows[0]);
      }
      return cached;
    }

    if (jobRequestsRef.current.has(cacheKey)) {
      return jobRequestsRef.current.get(cacheKey);
    }

    setJobOrderLoading(true);
    if (activeJobRequestRef.current && activeJobRequestRef.current.key !== cacheKey) {
      activeJobRequestRef.current.controller.abort();
    }
    const controller = new AbortController();
    activeJobRequestRef.current = { key: cacheKey, controller };
    const request = axios.get(`/api/v1/get-all-jobs/`, {
        params: {
          company_id: companyId,
          page,
          size: JOBS_PAGE_SIZE
        },
        signal: controller.signal,
      }).then(response => {
      const { rows: apiJobs, count, hasNext } = normalizeJobsResponse(response.data);

      const transformedJobs = apiJobs.map(job => ({
        name: [job.job_number, job.job_title].filter(Boolean).join(" - "),
        code: job.job_number,
        id: job.id ?? job.job_id ?? job.job_number,
        job_id: job.id ?? job.job_id ?? job.job_number,
      })).filter(job => job.code);
      const result = {
        rows: transformedJobs,
        page,
        count,
        hasNext,
      };
      jobCacheRef.current.set(cacheKey, result);
      setJobOptions(transformedJobs);
      setJobOrderPage(page);
      setJobOrderTotalCount(count);
      setJobOrderHasNextPage(hasNext);
      if (page === 1 && (forceSelectFirstJob || !selectedJob) && transformedJobs[0]) {
        hasFilterInteracted.current = true;
        setPipelinePage(1);
        setSelectedJob(transformedJobs[0]);
      }
      return result;
    }).catch(error => {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return null;
      console.error('Error fetching jobs:', error);
      setJobOptions([]);
      setJobOrderTotalCount(0);
      setJobOrderHasNextPage(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch jobs',
        life: 3000
      });
      throw error;
    }).finally(() => {
      jobRequestsRef.current.delete(cacheKey);
      if (activeJobRequestRef.current?.key === cacheKey) {
        activeJobRequestRef.current = null;
        setJobOrderLoading(false);
      }
    });
    jobRequestsRef.current.set(cacheKey, request);
    return request;
  };

  const fetchData = async ({ jobId = selectedJob?.job_id || "", page = pipelinePage, search = candidateName } = {}) => {
    const selectedCompanyId = selectedCompany?.company_id || companyId;
    if (!selectedCompanyId || !jobId) return;

    const normalizedSearch = search.trim();
    const requestKey = JSON.stringify({ companyId: selectedCompanyId, jobId, page, search: normalizedSearch });
    if (candidateDataCacheRef.current.has(requestKey)) {
      const cachedResult = candidateDataCacheRef.current.get(requestKey);
      setColumns(cachedResult.columns);
      setStageCounts(cachedResult.counts);
      setKanbanData(cachedResult.data);
      setOriginalData(cachedResult.data);
      return cachedResult.data;
    }
    if (candidateRequestsRef.current.has(requestKey)) {
      return candidateRequestsRef.current.get(requestKey);
    }

    if (activeCandidateRequestRef.current && activeCandidateRequestRef.current.key !== requestKey) {
      activeCandidateRequestRef.current.controller.abort();
    }

    const controller = new AbortController();
    activeCandidateRequestRef.current = { key: requestKey, controller };
    setLoading(true);

    const query = new URLSearchParams({
      company_id: String(selectedCompanyId),
      job_id: String(jobId),
      page: String(page),
      size: String(pipelinePageSize),
      candidate_name: normalizedSearch,
    });

    const request = axios.get(`/api/v1/job_candidates_by_stage/?${query.toString()}`, {
      signal: controller.signal,
    }).then(response => {
      const responseData = response.data;
      const resultRows = Array.isArray(responseData?.results)
        ? responseData.results
        : Array.isArray(responseData?.results?.data)
          ? responseData.results.data
          : Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData?.data?.results)
              ? responseData.data.results
              : [];
      const apiStages = resultRows;
      const returnedColumns = apiStages
        .filter(stage => stage?.stage_name || stage?.name || stage?.stage)
        .map(stage => {
          const title = stage.stage_name || stage.name || stage.stage;
          return {
            id: title.toLowerCase().replace(/[^a-zA-Z0-9]/g, ''),
            title,
          };
        });
      const uniqueColumns = returnedColumns.filter((column, index, list) =>
        list.findIndex(item => item.id === column.id) === index
      );
      const transformedData = transformApiData(apiStages, uniqueColumns);
      const counts = apiStages.reduce((result, stage) => {
        const title = stage?.stage_name || stage?.name || stage?.stage;
        if (title) {
          const id = title.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
          result[id] = Number(stage.count ?? stage.total_count ?? stage.candidate_count ?? stage.candidates?.length ?? 0);
        }
        return result;
      }, {});
      const cachedResult = { data: transformedData, columns: uniqueColumns, counts };
      candidateDataCacheRef.current.set(requestKey, cachedResult);
      setColumns(uniqueColumns);
      setStageCounts(counts);
      setKanbanData(transformedData);
      setOriginalData(transformedData);
      return transformedData;
    }).catch(error => {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return null;
      console.error('Error fetching data:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch Kanban data',
        life: 3000
      });
      return null;
    }).finally(() => {
      candidateRequestsRef.current.delete(requestKey);
      if (activeCandidateRequestRef.current?.key === requestKey) {
        activeCandidateRequestRef.current = null;
        setLoading(false);
      }
    });

    candidateRequestsRef.current.set(requestKey, request);
    return request;
  };

  const transformApiData = (apiData, columnsForData = columns) => {
  const transformedData = {};
  const stageNameMap = columnsForData.reduce((map, column) => {
    map[column.title.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')] = column.id;
    return map;
  }, {});

  columnsForData.forEach(column => {
    transformedData[column.id] = [];
  });

  apiData.forEach(stage => {
    const stageTitle = stage?.stage_name || stage?.name || stage?.stage;
    if (!stageTitle || !Array.isArray(stage.candidates)) return;
    const stageName = stageTitle.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
    const mappedStage = stageNameMap[stageName];
    if (mappedStage) {
      transformedData[mappedStage] = stage.candidates.map(candidate => ({
        id: `c${candidate.job_candidate_id}`,
        name: getCandidateFullName(candidate),
        jobTitle: candidate.job?.job_title,
        company: candidate.job?.company_name,
        startDate: candidate.job?.job_start_date,
        jobId: candidate.job?.job_number,
        recruiter: candidate.job?.hiring_manager_name,
        jobStatus: candidate.type,
        email: candidate.candidate_email,
        phone: candidate.phone,
        city: candidate.city,
        state: candidate.job?.location,
        rejected_round:
          candidate.rejected_round ||
          candidate.job_candidate?.rejected_round ||
          candidate.job?.rejected_round ||
          stage.rejected_round ||
          "",
        reason: candidate.reason || ""
      }));
    } else {
      console.warn(`Unmapped stage: ${stage.stage_name}`);
    }
  });
  return transformedData;
};

  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [selectedJobStatus, setSelectedJobStatus] = useState(null);
  const [candidateName, setCandidateName] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);

  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragSourceColumn, setDragSourceColumn] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [currentHoverColumn, setCurrentHoverColumn] = useState(null);

  const [expandedColumns, setExpandedColumns] = useState({});
  const CARDS_TO_SHOW = 4;

const handleClear = async () => {
  setLoading(true);
  try {
    skipNextFilterFetch.current = true;
    setSelectedRecruiter(null);
    setSelectedJobStatus(null);
    setCandidateName('');
    setSelectedCompany(null);
    setEmail('');
    setPhone('');
    setCity('');
    setState('');
    setSelectedJob(null);
    setPipelinePage(1);
    setStageCounts({});
    setColumns([]);
    setKanbanData({});
    setOriginalData({});

    toast.current?.show({
      severity: 'info',
      summary: 'Filters Cleared',
      detail: 'All filters have been cleared',
      life: 3000,
    });
  } finally {
    setLoading(false);
  }
};

const fetchFilteredData = async () => {
  return fetchData({
    jobId: selectedJob?.job_id || "",
    page: pipelinePage,
    search: candidateName,
  });
};

useEffect(() => {
  if (!companyId || !selectedJob || !hasFilterInteracted.current) return;

  const timer = setTimeout(() => {
    fetchFilteredData();
  }, 350);

  return () => clearTimeout(timer);
}, [candidateName, companyId, pipelinePage]);

  const toggleColumnExpand = (columnId) => {
    setExpandedColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  useEffect(() => {
    if (!isDragging || !containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const scrollSpeed = 15;
    const scrollThreshold = 100;

    if (scrollTimerRef.current) {
      clearInterval(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }

    if (dragPosition.x > containerRect.right - scrollThreshold) {
      scrollTimerRef.current = setInterval(() => {
        container.scrollLeft += scrollSpeed;
      }, 30);
    } else if (dragPosition.x < containerRect.left + scrollThreshold) {
      scrollTimerRef.current = setInterval(() => {
        container.scrollLeft -= scrollSpeed;
      }, 30);
    }

    return () => {
      if (scrollTimerRef.current) {
        clearInterval(scrollTimerRef.current);
      }
    };
  }, [isDragging, dragPosition]);

  const onDragOver = (event, columnId = null) => {
    event.preventDefault();
    setDragPosition({ x: event.clientX, y: event.clientY });
    if (columnId && columnId !== currentHoverColumn) {
      setCurrentHoverColumn(columnId);
    }
  };

  const onDragStart = (event, itemId, columnId) => {
    event.dataTransfer.setData('itemId', itemId);
    event.dataTransfer.setData('sourceColumnId', columnId);
    setDraggedItemId(itemId);
    setDragSourceColumn(columnId);
    setIsDragging(true);

    if (event.target.classList) {
      event.target.classList.add('dragging');
    }
  };

  const onDragEnter = (event, itemId) => {
    if (draggedItemId !== itemId) {
      setDragOverItemId(itemId);
    }
  };

const onDragEnd = (event) => {
  if (event.target.classList) {
    event.target.classList.remove('dragging');
  }
  setDragOverItemId(null);
  setIsDragging(false);
  setCurrentHoverColumn(null);
  setDraggedItemId(null);
  setDragSourceColumn(null);
  if (scrollTimerRef.current) {
    clearInterval(scrollTimerRef.current);
    scrollTimerRef.current = null;
  }
};

const onDrop = async (event, dropColumnId) => {
  event.preventDefault();
  event.stopPropagation();

  const itemId = event.dataTransfer.getData("itemId");
  const sourceColumnId = event.dataTransfer.getData("sourceColumnId");

  if (!itemId || !sourceColumnId || !draggedItemId) return;

  const sourceIndex = columns.findIndex(col => col.id === sourceColumnId);
  const targetIndex = columns.findIndex(col => col.id === dropColumnId);
  if (sourceIndex < targetIndex) {
    setCurrentMove({
      itemId,
      sourceColumnId,
      dropColumnId,
    });
    setRejectedRound("");
    setShowReasonModal(true);
  } else {
    toast.current?.show({
      severity: "warn",
      summary: "Warning",
      detail: "Moving backward is not allowed.",
      life: 3000,
    });
  }
};
useEffect(() => {
  return () => {
    setDraggedItemId(null);
    setDragSourceColumn(null);
    setDragOverItemId(null);
    setIsDragging(false);
    setCurrentHoverColumn(null);
  };
}, []);

const [isSubmitting, setIsSubmitting] = useState(false);
const [rejectedRound, setRejectedRound] = useState("");
const handleConfirmMove = async () => {
  const { itemId, sourceColumnId, dropColumnId } = currentMove;
  if (!itemId || !sourceColumnId || !dropColumnId || sourceColumnId === dropColumnId) {
    setShowReasonModal(false);
    setMoveReason("");
    setRejectedRound("");
    setCurrentMove({
      itemId: null,
      sourceColumnId: null,
      dropColumnId: null,
    });
    return;
  }
  const stageNameMap = columns.reduce((map, column) => {
    map[column.id] = column.title;
    return map;
  }, {});
  const apiStageName = stageNameMap[dropColumnId];
  const isRejectedStage = String(apiStageName || "").toLowerCase() === "rejected";

  if (isRejectedStage && !rejectedRound) {
    toast.current?.show({
      severity: "warn",
      summary: "Rejected At required",
      detail: "Select whether the candidate was rejected by HR or by Panel.",
      life: 3000,
    });
    return;
  }

  setIsSubmitting(true);
  try {
    const jobCandidateId = itemId.replace('c', '');
    const payload = {
      stage: apiStageName,
      reason: moveReason,
      ...(isRejectedStage ? { rejected_round: rejectedRound } : {}),
    };
    await axios.put(`/api/v1/change-stage/${jobCandidateId}/?company_id=${companyId}`, payload);
    const newKanbanData = { ...kanbanData };
    let movedItem = null;
    const sourceColumnItems = [...newKanbanData[sourceColumnId]];
    const itemIndex = sourceColumnItems.findIndex(item => item.id === itemId);
    if (itemIndex !== -1) {
      movedItem = sourceColumnItems[itemIndex];
      sourceColumnItems.splice(itemIndex, 1);
      newKanbanData[sourceColumnId] = sourceColumnItems;
    }
    const targetColumnItems = [...newKanbanData[dropColumnId]];
    if (movedItem) {
      movedItem.moveReason = moveReason;
      targetColumnItems.push(movedItem);
      newKanbanData[dropColumnId] = targetColumnItems;
    }

    setKanbanData(newKanbanData);
    await fetchData();

    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail: `Stage updated successfully. Notifications queued. Current stage: ${apiStageName}`,
      life: 3000,
    });
  } catch (error) {
    console.error('Error updating stage:', error);
    const backendMessage =
      error.response?.data?.error ||
      error.response?.data?.detail ||
      'Failed to update candidate stage';
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: backendMessage,
      life: 3000,
    });
  } finally {
    setIsSubmitting(false);
    setShowReasonModal(false);
    setMoveReason("");
    setRejectedRound("");
    setCurrentMove({
      itemId: null,
      sourceColumnId: null,
      dropColumnId: null,
    });
  }
};


  const handleColumnDragOver = (event, columnId) => {
    onDragOver(event, columnId);

    if (containerRef.current) {
      const columnElement = document.getElementById(`column-${columnId}`);
      if (columnElement) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const columnRect = columnElement.getBoundingClientRect();

        if (columnRect.left > containerRect.right) {
          containerRef.current.scrollLeft += 10;
        } else if (columnRect.right < containerRect.left) {
          containerRef.current.scrollLeft -= 10;
        }
      }
    }
  };

  const renderCandidateCard = (item, columnId) => {
    const avatar = getAvatarStyle(item.name);
    return (
      <div
        key={item.id}
        id={`card-${item.id}`}
        draggable={true}
        onDragStart={(e) => onDragStart(e, item.id, columnId)}
        onDragEnd={onDragEnd}
        onDragEnter={(e) => onDragEnter(e, item.id)}
        onDragOver={onDragOver}
        className={`cursor-move kanban-card-wrap ${dragOverItemId === item.id ? 'drag-over' : ''}`}
      >
        <div className="kanban-candidate-card">
          <div className="kcc-top">
            <span
              className="kcc-avatar"
              style={{ background: avatar.bg, color: avatar.color }}
            >
              {getInitials(item.name)}
            </span>
            <div className="kcc-title-block">
              <div className="kcc-name">{item.name}</div>
              <div className="kcc-jobtitle">{item.jobTitle}</div>
            </div>
          </div>
          <div className="kcc-company">{item.company}</div>
          <div className="kcc-meta-row">
            <span className="kcc-jobid-badge">{item.jobId}</span>
            {item.startDate && <span className="kcc-start">Start: {item.startDate}</span>}
          </div>
          {item.rejected_round && (
            <div className="kcc-rejected-round">
              <span>Rejected:</span> {item.rejected_round}
            </div>
          )}
          {item.reason && (
            <div className="kcc-reason">
              <span>Reason:</span> {item.reason}
            </div>
          )}
        </div>
        {dragOverItemId === item.id && draggedItemId !== item.id && (
          <div className="drop-indicator" />
        )}
      </div>
    );
  };

  const getVisibleCards = (columnId) => {
    const cards = kanbanData[columnId] || [];
    if (cards.length <= CARDS_TO_SHOW || expandedColumns[columnId]) {
      return cards;
    }
    return cards.slice(0, CARDS_TO_SHOW);
  };

  const hasMoreCards = (columnId) => {
    const cards = kanbanData[columnId] || [];
    return cards.length > CARDS_TO_SHOW;
  };

  useEffect(() => {
    const handleGlobalDragOver = (e) => {
      if (isDragging) {
        e.preventDefault();
        setDragPosition({ x: e.clientX, y: e.clientY });
      }
    };

    document.addEventListener('dragover', handleGlobalDragOver);

    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
    };
  }, [isDragging]);

  const [value, setValue] = useState('');
  const [items, setItems] = useState([]);

  const names = [
    "Infosys", "Cognizant", "Capgemini", "CA Technologies", "L&T Mindtree",
    "Ram Software Services", "Citel Global Communication", "Tarun Digital Media",
    "Sai Digital Media", "Mahesh Digital Media", "Twitter", "Uber", "TCS",
    "Wipro", "HCL", "Deloitte", "Accenture", "IBM", "SAP", "Dell", "HP",
    "Cisco", "Adobe", "PayPal", "Spotify", "Atlassian"
  ];

  const search = (event) => {
    let filteredNames = names.filter(name => name.toLowerCase().includes(event.query.toLowerCase()));
    setItems(filteredNames);
  };
const [showReasonModal, setShowReasonModal] = useState(false);
const [moveReason, setMoveReason] = useState("");
const [currentMove, setCurrentMove] = useState({
  itemId: null,
  sourceColumnId: null,
  dropColumnId: null,
});
useEffect(() => {
  if (!companyId) {
    setJobOptions([]);
    setColumns([]);
    setKanbanData({});
    setOriginalData({});
    return;
  }

  setSelectedJob(null);
  setCandidateName("");
  setPipelinePage(1);
  setStageCounts({});
  setKanbanData({});
  setOriginalData({});
  setColumns([]);
  Promise.all([
    fetchColumns(),
    fetchRecruiters(),
    fetchJobStatuses(),
    fetchJobs(1, true),
  ]);
}, [companyId, companySelectionVersion]);

useEffect(() => {
  if (!companyId || !selectedJob) return;

  let cancelled = false;
  const loadPipeline = async () => {
    setLoading(true);
    if (cancelled) return;
    await fetchData({
      jobId: selectedJob.job_id || "",
      page: 1,
      search: candidateName,
    });
  };

  loadPipeline();
  return () => {
    cancelled = true;
  };
}, [companyId, selectedJob]);

  const targetStageTitle = columns.find(col => col.id === currentMove.dropColumnId)?.title;
  const movingCandidate = findCandidateById(kanbanData, currentMove.itemId);
  const totalPipelineCandidates = Object.keys(stageCounts).length > 0
    ? Object.values(stageCounts).reduce((total, count) => total + Number(count || 0), 0)
    : Object.values(kanbanData).reduce(
      (total, items) => total + (Array.isArray(items) ? items.length : 0),
      0
    );
  const jobOrderTotalPages = jobOrderTotalCount
    ? Math.max(1, Math.ceil(jobOrderTotalCount / JOBS_PAGE_SIZE))
    : Math.max(1, jobOrderPage + (jobOrderHasNextPage ? 1 : 0));
  const canGoToPreviousJobOrderPage = jobOrderPage > 1 && !jobOrderLoading;
  const canGoToNextJobOrderPage = !jobOrderLoading
    && (jobOrderHasNextPage || jobOrderPage < jobOrderTotalPages);

  const jobOrderPanelFooterTemplate = () => (
    <div className="kanban-filter-pagination-footer" onMouseDown={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="kanban-filter-page-btn"
        disabled={!canGoToPreviousJobOrderPage}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          fetchJobs(jobOrderPage - 1);
        }}
      >
        Previous
      </button>
      <span className="kanban-filter-page-count">
        Page {jobOrderPage} of {jobOrderTotalPages}
      </span>
      <button
        type="button"
        className="kanban-filter-page-btn"
        disabled={!canGoToNextJobOrderPage}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          fetchJobs(jobOrderPage + 1);
        }}
      >
        Next
      </button>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Toast ref={toast} />
        <Container fluid>
          <div className="page-title-box actjobbread">
            <div className="agent-list-hero candidate-outcome-hero candidate-outcome-hero-selected kanban-pipeline-hero">
              <div className="agent-list-title">
                <div className="agent-list-hero-icon">
                  <FaUserCheck size={21} />
                </div>
                <div>
                  <h1>Candidate Pipeline</h1>
                  <p>Track candidates by stage with job filters and pipeline actions.</p>
                </div>
              </div>
              <div className="kanban-pipeline-actions">
                <div className="kanban-pipeline-controls">
                  <div className="candidate-outcome-company kanban-pipeline-company">
                    <label className="d-block">
                      Select Company <span className="text-danger">*</span>
                    </label>
                    <CompanySelector
                      onCompanyChange={handleCompanyChange}
                      selectedCompanyId={companyId}
                      showAllOption
                      allOptionValue="all"
                      allOptionLabel="All"
                      className="kanban-filter-company-select"
                    />
                  </div>
                  <Dropdown
                    value={selectedJob}
                    onChange={(e) => {
                      hasFilterInteracted.current = true;
                      candidateDataCacheRef.current.clear();
                      setPipelinePage(1);
                      setSelectedJob(e.value);
                    }}
                    options={jobOptions}
                    optionLabel="name"
                    placeholder="Job Order"
                    disabled={!companyId}
                    filter
                    filterBy="name,code"
                    loading={jobOrderLoading}
                    className="kanban-filter-dropdown kanban-filter-joborder-dropdown"
                    panelClassName="kanban-filter-dropdown-panel"
                    panelFooterTemplate={jobOrderPanelFooterTemplate}
                  />
                  
                  <InputText
                    value={candidateName}
                    onChange={(e) => {
                      hasFilterInteracted.current = true;
                      setPipelinePage(1);
                      setCandidateName(e.target.value);
                    }}
                    placeholder="Candidate"
                    className="kanban-filter-input"
                  />
                  <button
                    type="button"
                    className="btn kanban-filter-clear-btn"
                    disabled={loading}
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <div className="agent-list-count-card kanban-pipeline-count-card">
                    <span>Total Candidates</span>
                    <strong>{totalPipelineCandidates}</strong>
                  </div>
                </div>
              </div>
            </div>
            {loading ? (
              <OrgTableShimmer columns={5} rows={7} titleWidth="220px" />
              ) : columns.length === 0 ? (
                <div
                  style={{
                    minHeight: "calc(100vh - 230px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    role="status"
                    style={{
                    width: "min(680px, calc(100% - 32px))",
                    minHeight: "300px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: "48px 24px",
                    border: "1px solid #d8e6f3",
                    borderRadius: 14,
                    background: "#f8fbff",
                    boxShadow: "0 10px 28px rgba(23, 50, 77, 0.08)",
                    color: "#52667a",
                    }}
                  >
                  <div
                    style={{
                      width: 76,
                      height: 76,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      background: "#eef6ff",
                      border: "1px solid #cfe1f2",
                      color: "#176899",
                      fontSize: 32,
                      marginBottom: 18,
                    }}
                  >
                    <FaInbox aria-hidden="true" />
                  </div>
                  <h3 style={{ margin: "0 0 8px", color: "#17324d", fontSize: 20, fontWeight: 800 }}>
                    {selectedJob ? "No pipeline data found" : "Select a job to view the pipeline"}
                  </h3>
                  <p style={{ maxWidth: 440, margin: 0, lineHeight: 1.6, fontSize: 14 }}>
                    {selectedJob
                      ? "There are no candidates or stages available for the selected job. Try another job or clear the filters."
                      : "Choose a job order above to load candidates grouped by pipeline stage."}
                  </p>
                  </div>
                </div>
              ) : (
            <Row className="pt-3">
              <Col lg={12}>
                <div
                  ref={containerRef}
                  className="kanban-row"
                  style={{
                    display: 'flex',
                    gap: '16px',
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    maxHeight: '78vh',
                    paddingBottom: '4px',
                    scrollBehavior: 'smooth',
                    position: 'relative',
                  }}
                >
                  {columns.map((column, colIndex) => {
                    const accent = getColumnAccent(colIndex);
                    return (
                    <div
                      key={column.id}
                      id={`column-${column.id}`}
                      className={`flex-none kanban-view-col ${currentHoverColumn === column.id ? 'column-hover' : ''}`}
                      onDragOver={(e) => handleColumnDragOver(e, column.id)}
                      onDrop={(e) => onDrop(e, column.id)}
                    >
                      <div className="kanban-col-shell" style={{ borderTop: `3px solid ${accent.bar}` }}>
                        <div className="kanban-col-header" style={{ background: accent.bg }}>
                          <h3 className="kanban-col-title" style={{ color: accent.text }}>{column.title}</h3>
                          <span className="kanban-col-count" style={{ background: accent.bar }}>
                            {stageCounts[column.id] ?? kanbanData[column.id]?.length ?? 0}
                          </span>
                        </div>
                        <div
                          className="kanban-col-body"
                          onDragOver={(e) => onDragOver(e, column.id)}
                        >
                          {getVisibleCards(column.id).length === 0 ? (
                            <div className="kanban-col-empty">No candidates</div>
                          ) : (
                            getVisibleCards(column.id).map((item) => renderCandidateCard(item, column.id))
                          )}
                          {hasMoreCards(column.id) && (
                            <div className="text-center mt-2">
                              <button
                                type="button"
                                className="kanban-showmore-btn"
                                onClick={() => toggleColumnExpand(column.id)}
                              >
                                {expandedColumns[column.id]
                                  ? "Show Less"
                                  : `Show More (${kanbanData[column.id].length - CARDS_TO_SHOW})`}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )})}
                  {isDragging && (
                    <>
                      <div
                        className="scroll-indicator left"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '20px',
                          background: 'linear-gradient(to right, rgba(59, 130, 246, 0.2), transparent)',
                          pointerEvents: 'none',
                          zIndex: 10
                        }}
                      />
                      <div
                        className="scroll-indicator right"
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '20px',
                          background: 'linear-gradient(to left, rgba(59, 130, 246, 0.2), transparent)',
                          pointerEvents: 'none',
                          zIndex: 10
                        }}
                      />
                    </>
                  )}
                </div>
              </Col>
            </Row>)}
          </div>
        </Container>

        <style jsx>{`
          .kanban-modern-page {
            background: linear-gradient(180deg, #f3f7fb 0%, #eef4f8 100%);
          }
          .kanban-pipeline-hero {
            align-items: center;
            margin-bottom: 12px;
          }
          .kanban-pipeline-actions {
            display: flex;
            flex: 1 1 auto;
            min-width: 560px;
            flex-direction: column;
            align-items: flex-end;
            gap: 8px;
          }
          .kanban-pipeline-controls {
            display: flex;
            width: 100%;
            align-items: flex-end;
            justify-content: flex-end;
            gap: 8px;
            flex-wrap: wrap;
          }
          .kanban-pipeline-company {
            width: 230px;
            min-width: 230px;
          }
          .kanban-pipeline-company label {
            margin-bottom: 6px;
            color: #ffffff;
            font-size: 12px;
            font-weight: 700;
          }
          .kanban-pipeline-hero .agent-list-count-card {
            margin-top: 0 !important;
          }
          .kanban-pipeline-hero .kanban-pipeline-count-card {
            align-self: flex-end !important;
            margin-bottom: 0 !important;
          }
          .kanban-filter-company-select.form-select {
            width: 100%;
            height: 38px;
            min-height: 38px;
            border: 1px solid #d1d5db !important;
            border-radius: 6px !important;
            background-color: #fff !important;
            color: #1f2937;
            font-size: 14px;
            box-shadow: none !important;
          }
          .kanban-filter-dropdown.p-dropdown {
            width: 166px;
            height: 38px;
            min-height: 38px;
            border: 1px solid #d1d5db !important;
            border-radius: 6px !important;
            background: #fff !important;
            box-shadow: none !important;
          }
          .kanban-filter-joborder-dropdown.p-dropdown {
            width: 230px;
          }
          .kanban-filter-pagination-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 8px;
            border-top: 1px solid #e2e8f0;
            background: #fff;
          }
          .kanban-filter-page-btn {
            min-width: 72px;
            height: 30px;
            border: 1px solid #cbd5e1;
            border-radius: 6px;
            background: #f8fafc;
            color: #334155;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
          }
          .kanban-filter-page-btn:not(:disabled):hover {
            background: #eef6ff;
            border-color: #90b4d4;
            color: #0f4c81;
          }
          .kanban-filter-page-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }
          .kanban-filter-page-count {
            color: #64748b;
            font-size: 12px;
            font-weight: 700;
            white-space: nowrap;
          }
          .kanban-filter-dropdown .p-dropdown-label {
            display: flex;
            align-items: center;
            padding: 0 12px;
            color: #1f2937;
            font-size: 14px;
            line-height: 38px;
          }
          .kanban-filter-dropdown .p-dropdown-trigger {
            width: 36px;
            color: #334155;
          }
          .kanban-filter-dropdown-panel {
            width: 240px !important;
            max-width: 240px !important;
          }
          .kanban-filter-dropdown-panel .p-dropdown-items-wrapper {
            max-height: 220px !important;
          }
          .kanban-filter-dropdown-panel .p-dropdown-item {
            min-height: 34px !important;
            padding: 8px 12px !important;
            font-size: 13px !important;
            line-height: 1.3 !important;
            white-space: normal !important;
          }
          .kanban-filter-input.p-inputtext {
            width: 130px;
            height: 38px;
            min-height: 38px;
            border: 1px solid #d1d5db !important;
            border-radius: 6px !important;
            background: #fff !important;
            color: #1f2937;
            font-size: 14px;
            box-shadow: none !important;
          }
          .kanban-filter-clear-btn {
            height: 38px;
            min-height: 38px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            line-height: 1;
            white-space: nowrap;
          }
          .kanban-filter-clear-btn {
            min-width: 62px;
            padding: 0 14px;
            border: 1px solid #d1d5db;
            background: #fff;
            color: #475569;
          }
          .kanban-filter-clear-btn:hover {
            background: #f8fafc;
            color: #334155;
          }
          .kanban-filter-clear-btn:disabled {
            cursor: not-allowed;
            opacity: 0.86;
            border-color: #d1d5db !important;
            background: #fff !important;
            color: #475569 !important;
          }
          @media (max-width: 768px) {
            .kanban-pipeline-hero,
            .kanban-pipeline-actions,
            .kanban-pipeline-controls {
              align-items: stretch;
            }
            .kanban-pipeline-actions {
              min-width: 0;
              width: 100%;
            }
            .kanban-pipeline-company {
              width: 100%;
              min-width: 0;
            }
            .kanban-filter-dropdown.p-dropdown,
            .kanban-filter-input.p-inputtext {
              width: 100%;
              flex: 1 1 100%;
            }
          }
          .dragging {
            opacity: 0.5;
          }
          .drag-over {
            transform: translateY(2px);
          }
          .column-hover {
            z-index: 5;
          }
          .kanban-view-col {
            min-width: 300px;
            width: 300px;
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
          }
          .kanban-col-shell {
            background: #fff;
            border: 1px solid #e3ecf7;
            border-radius: 12px;
            box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            height: 100%;
          }
          .kanban-col-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 14px;
            border-bottom: 1px solid #e9eef5;
          }
          .kanban-col-title {
            margin: 0;
            font-size: 14px;
            font-weight: 800;
          }
          .kanban-col-count {
            min-width: 26px;
            height: 22px;
            padding: 0 8px;
            border-radius: 999px;
            color: #fff;
            font-size: 12px;
            font-weight: 700;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .kanban-col-body {
            height: 65vh;
            padding: 10px;
            overflow-y: auto;
            overflow-x: hidden;
            background: #fafbfd;
            scrollbar-width: none;
          }
          .kanban-col-body::-webkit-scrollbar {
            width: 0;
          }
          .kanban-col-body::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          .kanban-col-shell:hover .kanban-col-body,
          .kanban-col-body:focus-within {
            scrollbar-width: thin;
          }
          .kanban-col-shell:hover .kanban-col-body::-webkit-scrollbar,
          .kanban-col-body:focus-within::-webkit-scrollbar {
            width: 6px;
          }
          .kanban-col-empty {
            text-align: center;
            color: #94a3b8;
            font-size: 13px;
            padding: 24px 8px;
          }
          .kanban-card-wrap {
            cursor: move;
            transition: all 0.15s ease;
            position: relative;
            margin-bottom: 10px;
          }
          .kanban-candidate-card {
            background: #fff;
            border: 1px solid #e6ecf3;
            border-radius: 10px;
            padding: 12px;
            box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
            transition: box-shadow 0.15s ease, border-color 0.15s ease;
          }
          .kanban-candidate-card:hover {
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
            border-color: #cbd8e8;
          }
          .kcc-top {
            display: flex;
            align-items: flex-start;
            gap: 8px;
          }
          .kcc-avatar {
            width: 30px;
            height: 30px;
            min-width: 30px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 800;
          }
          .kcc-title-block {
            min-width: 0;
            flex: 1;
          }
          .kcc-name {
            font-weight: 700;
            color: #102a43;
            font-size: 12.5px;
            line-height: 1.3;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .kcc-jobtitle {
            font-size: 12px;
            color: #475569;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .kcc-company {
            font-size: 11px;
            color: #94a3b8;
            margin-top: 6px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .kcc-meta-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 8px;
            gap: 8px;
          }
          .kcc-jobid-badge {
            display: inline-flex;
            align-items: center;
            padding: 2px 8px;
            border-radius: 999px;
            background: #eef2f7;
            color: #334155;
            font-size: 10.5px;
            font-weight: 700;
            white-space: nowrap;
          }
          .kcc-start {
            font-size: 10.5px;
            color: #94a3b8;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .kcc-reason {
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px dashed #e2e8f0;
            font-size: 11px;
            color: #64748b;
          }
          .kcc-rejected-round {
            margin-top: 8px;
            font-size: 11px;
            color: #b91c1c;
            font-weight: 600;
          }
          .kcc-rejected-round span {
            font-weight: 700;
          }
          .kcc-reason span {
            font-weight: 700;
            color: #475569;
          }
          .drop-indicator {
            position: absolute;
            top: -6px;
            left: 0;
            right: 0;
            height: 4px;
            background-color: #3b82f6;
            border-radius: 2px;
            z-index: 10;
          }
          .kanban-showmore-btn {
            width: 100%;
            padding: 8px;
            border: 1px dashed #cbd5e1;
            border-radius: 8px;
            background: #fff;
            color: #334155;
            font-size: 12.5px;
            font-weight: 700;
            cursor: pointer;
          }
          .kanban-showmore-btn:hover {
            background: #f1f5f9;
          }
          .kanban-row {
            scrollbar-width: thin;
          }
          .kanban-row::-webkit-scrollbar {
            height: 8px;
          }
          .kanban-row::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.25);
            border-radius: 4px;
          }
          .kanban-row::-webkit-scrollbar-track {
            background-color: transparent;
          }
        `}</style>
      </div>

      <Modal
        isOpen={showReasonModal}
        toggle={() => setShowReasonModal(false)}
        centered
        contentClassName="kanban-reason-modal-content"
      >
        <div className="kanban-reason-modal-header">
          <div className="kanban-reason-modal-header-icon">
            <FaExchangeAlt />
          </div>
          <div className="kanban-reason-modal-header-text">
            <div className="kanban-reason-modal-title">Move Candidate</div>
            <div className="kanban-reason-modal-subtitle">
              {movingCandidate?.name || "Candidate"} moving to <strong>{targetStageTitle || "next stage"}</strong>
            </div>
          </div>
          <button
            type="button"
            className="kanban-reason-modal-close"
            onClick={() => setShowReasonModal(false)}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <ModalBody className="kanban-reason-modal-body">
          <div className="kanban-reason-candidate-box">
            <span className="kanban-reason-candidate-label">Candidate</span>
            <strong>{movingCandidate?.name || "--"}</strong>
            {movingCandidate?.jobTitle ? <span>{movingCandidate.jobTitle}</span> : null}
          </div>
          <label className="kanban-reason-label">
            Enter Reason <span className="text-danger">*</span>
          </label>
          <textarea
            className="kanban-reason-textarea"
            value={moveReason}
            onChange={(e) => setMoveReason(e.target.value)}
            placeholder="Why are you moving this candidate?"
            rows={4}
          />
          {String(targetStageTitle || "").toLowerCase() === "rejected" && (
            <>
              <label className="kanban-reason-label" htmlFor="kanban-rejected-round">
                Rejected At <span className="text-danger">*</span>
              </label>
              <select
                id="kanban-rejected-round"
                className="kanban-reason-select"
                value={rejectedRound}
                onChange={(e) => setRejectedRound(e.target.value)}
              >
                <option value="">Select rejected at</option>
                <option value="By HR">Rejected by HR</option>
                <option value="By Panel">Rejected by Panel</option>
              </select>
            </>
          )}
        </ModalBody>

        <ModalFooter className="kanban-reason-modal-footer">
          <button
            className="kanban-reason-cancel-btn"
            onClick={() => setShowReasonModal(false)}
          >
            Cancel
          </button>
          <span
            title={
              !moveReason.trim()
                ? "Please enter reason"
                : String(targetStageTitle || "").toLowerCase() === "rejected" && !rejectedRound
                  ? "Please select rejected at"
                  : ""
            }
          >
            <button
              className="kanban-reason-submit-btn"
              onClick={handleConfirmMove}
              disabled={
                isSubmitting ||
                !moveReason.trim() ||
                (String(targetStageTitle || "").toLowerCase() === "rejected" && !rejectedRound)
              }
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </span>
        </ModalFooter>
      </Modal>

      <style jsx global>{`
        .kanban-reason-modal-content {
          border: 0 !important;
          border-radius: 16px !important;
          overflow: hidden;
          box-shadow: 0 22px 60px rgba(15, 23, 42, 0.22);
        }
        .kanban-reason-modal-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 20px;
          background: linear-gradient(135deg, #12395c 0%, #0f766e 100%);
          color: #fff;
        }
        .kanban-reason-modal-header-icon {
          width: 38px;
          height: 38px;
          min-width: 38px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.16);
          border: 1px solid rgba(255, 255, 255, 0.24);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
        }
        .kanban-reason-modal-header-text {
          flex: 1;
          min-width: 0;
        }
        .kanban-reason-modal-title {
          font-size: 16px;
          font-weight: 800;
          line-height: 1.25;
        }
        .kanban-reason-modal-subtitle {
          margin-top: 2px;
          font-size: 12.5px;
          color: rgba(255, 255, 255, 0.82);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .kanban-reason-modal-subtitle strong {
          color: #fff;
        }
        .kanban-reason-modal-close {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.16);
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
        }
        .kanban-reason-modal-close:hover {
          background: rgba(255, 255, 255, 0.28);
        }
        .kanban-reason-modal-body {
          padding: 22px 22px 8px;
        }
        .kanban-reason-candidate-box {
          display: flex;
          flex-direction: column;
          gap: 3px;
          margin-bottom: 14px;
          padding: 10px 12px;
          border: 1px solid #d9e4ef;
          border-radius: 10px;
          background: #f8fafc;
        }
        .kanban-reason-candidate-label {
          color: #64748b;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
        }
        .kanban-reason-candidate-box strong {
          color: #102a43;
          font-size: 13.5px;
          line-height: 1.25;
        }
        .kanban-reason-candidate-box span:last-child {
          color: #64748b;
          font-size: 12px;
        }
        .kanban-reason-label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 700;
          color: #334155;
        }
        .kanban-reason-textarea {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #d9e4ef;
          border-radius: 10px;
          font-size: 13.5px;
          color: #1e293b;
          resize: vertical;
          min-height: 96px;
          background: #f8fafc;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .kanban-reason-textarea:focus {
          outline: none;
          border-color: #0f766e;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
        }
        .kanban-reason-modal-footer {
          justify-content: center !important;
          gap: 10px;
          padding: 16px 22px 22px;
          border-top: 1px solid #eef2f7;
        }
        .kanban-reason-cancel-btn,
        .kanban-reason-submit-btn {
          min-width: 108px;
          height: 40px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 13.5px;
          cursor: pointer;
        }
        .kanban-reason-cancel-btn {
          border: 1px solid #d9e4ef;
          background: #fff;
          color: #475569;
        }
        .kanban-reason-cancel-btn:hover {
          background: #f1f5f9;
        }
        .kanban-reason-submit-btn {
          border: none;
          background: linear-gradient(135deg, #12395c, #0f766e);
          color: #fff;
          box-shadow: 0 6px 14px rgba(15, 118, 110, 0.22);
        }
        .kanban-reason-submit-btn:hover:not(:disabled) {
          filter: brightness(1.05);
        }
        .kanban-reason-submit-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
      `}</style>
    </React.Fragment>
  );
};

export default KanbanView;
